import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";

import SessionManager from "@Core/session";
import { IActivityMetaDetails } from "@Models/IActivityDetails";
import { isNil, isPresent, useSearchParams } from "@Utils";
import {
    Button,
    Checkbox,
    Col,
    DatePicker,
    Divider,
    Form,
    Input,
    Progress,
    Row,
    Select,
    Typography,
    Upload,
    notification,
} from "antd";
import { RcFile, UploadProps } from "antd/lib/upload";
import useActivityUpload from "./hooks/useActivityUpload";
import useUpdateActivityMetadata from "./hooks/useUpdateActivityMetadata";
import { Clear, DeleteOutlined, DownloadDoneOutlined, ErrorOutlineOutlined, UploadOutlined } from "@mui/icons-material";
import { dateFormatList, isDevelopment } from "@Utils";
import useCurrentActivityUpload from "./hooks/useCurrentActivityUpload";
import { IModalState } from "@Constants";
import IconButton from "@Components/shared/IconButton";
import useQuestionnaireInstance from "@Components/questionnaires/hooks/useQuestionnaireInstance";
import useQuestionnaireSelection from "@Components/activity/hooks/useQuestionnaireSelection";

export const VALID_TYPES = ["video/avi", "video/mp4", "video/quicktime"];

interface IProps {
    handleClose: () => void;
    customActivityId: string;
    customActivityCategoryId: string;
}

const ActivityUpload: React.FC<IProps> = ({ handleClose, customActivityId, customActivityCategoryId }) => {
    const { userId } = SessionManager.getServiceUser();
    const { percentage, startUpload, uploadStatus, resetUpload, abortUpload, resumeUpload, currentUpload } =
        useActivityUpload();
    const { modalState, setModalState } = useCurrentActivityUpload();
    const { questionnaireAssociationsActive } = useQuestionnaireInstance({
        activityId: customActivityId,
        activityCategoryId: customActivityCategoryId,
    });
    const { canShowSelect } = useQuestionnaireSelection();

    // ** Search params ** //
    const [, setSearchParams] = useSearchParams();

    const { onUpdateActivityMetadata } = useUpdateActivityMetadata();

    const [form] = Form.useForm();

    // ** State Hooks ** //
    const [uploadId, setUploadId] = useState<string>();
    const [isCMEPublic, setIsCMEPublic] = useState<boolean>(false);

    useEffect(() => {
        setIsCMEPublic(form.getFieldValue("isCMEPublic"));
    }, [form]);

    const uploadProps: UploadProps = {
        accept: VALID_TYPES.join(", "),
        beforeUpload: (file) => handleBeforeUpload(file),
        className: "ant-upload-video",
        name: "file",
        showUploadList: false,
    };

    const resumeUploadProps: UploadProps = {
        accept: VALID_TYPES.join(", "),
        beforeUpload: (file) => handleBeforeUploadResume(file),
        className: "ant-upload-video",
        name: "file",
        showUploadList: false,
    };

    // ** Handlers ** //

    /**
     * Validate the filetype
     */
    const handleBeforeUpload = (file: RcFile) => {
        const valid = VALID_TYPES.includes(file.type);
        const fileSize = file.size;
        const maxFileSize = 100000000; //100mb
        if (!valid || !file) {
            notification.error({ message: "Video file type is invalid" });
        } else if (fileSize > maxFileSize) {
            notification.error({ message: "Video file must be under 100MB" });
        } else {
            // we will give the user the ability to confirm the file before beginning the upload
            const newModalState = { ...modalState, status: IModalState.Continue, pendingFile: file };
            setModalState(newModalState);
        }

        // returning false prevents the ant d upload request because we handle that elsewhere
        return false;
    };

    /**
     * Validate the filetype on upload resume
     */
    const handleBeforeUploadResume = (file: RcFile) => {
        const valid = VALID_TYPES.includes(file.type);
        if (!valid || !file) {
            notification.error({ message: "Video file type is invalid" });
        }

        if (!currentUpload) {
            return notification.error({ message: "Unable to resume your upload." });
        }

        const isSameFile = file.name === currentUpload.fileDetails.name && file.size === currentUpload.fileDetails.size;
        if (!isSameFile) return notification.error({ message: "Please select the same file." });
        if (!currentUpload) return notification.error({ message: "Unable to resume your upload." });

        // we will give the user the ability to confirm the file before beginning the upload
        const newModalState = { ...modalState, status: IModalState.Uploading, pendingFile: file };
        setModalState(newModalState);

        //start the resume upload instantly without user needing to click on upload again
        resumeUpload({ file, uploadId: currentUpload?.uploadId }, currentUpload);

        // returning false prevents the ant d upload request because we handle that elsewhere
        return false;
    };

    /**
     * Start the upload
     */
    const handleConfirmUpload = async () => {
        if (isNil(modalState.pendingFile)) return null;

        const result = await startUpload({ file: modalState.pendingFile });
        if (!result) return null; // no result means the upload was not started

        setUploadId(result.uploadId);
        const newModalState = {
            ...modalState,
            activityInstanceId: result.activityInstanceId,
            status: IModalState.Uploading,
        };
        setModalState(newModalState);

        return result;
    };

    const handleAbortUpload = async () => {
        if (isNil(currentUpload)) return;

        await abortUpload(currentUpload, (id: string) => {
            if (uploadId && id === modalState.activityInstanceId) {
                const newModalState = {
                    ...modalState,
                    activityInstanceId: undefined,
                    pendingFile: undefined,
                    metadata: undefined,
                    status: IModalState.Uploading,
                };
                setModalState(newModalState);
                setUploadId(undefined);
                form.resetFields();
            }
        });
    };

    const handleSaveAndExit = useCallback(() => {
        const activityMetadata = form?.getFieldsValue();
        setModalState({ ...modalState, metadata: activityMetadata });
        handleClose();
    }, [modalState, form]);

    const reset = useCallback(() => {
        setUploadId(undefined);
        resetUpload();
        const newModalState = {
            ...modalState,
            status: IModalState.Standard,
            pendingFile: undefined,
            metadata: undefined,
            activityInstanceId: undefined,
        };
        setModalState(newModalState);
        setIsCMEPublic(false);
    }, [modalState]);

    const handleUpdate = async () => {
        let result;
        try {
            // Validate the form fields
            const isValid = await form
                .validateFields()
                .then(() => true)
                .catch(() => false);

            // If the form is not valid, stop execution
            if (!isValid) {
                return;
            }
            // Proceed with the upload if the form is valid
            result = await handleConfirmUpload();
        } catch (error) {}
        if (result) {
            const activityMetadata = form?.getFieldsValue();
            setModalState({
                ...modalState,
                activityInstanceId: result.activityInstanceId,
                metadata: activityMetadata,
                status: IModalState.Uploading,
            });

            const values: IActivityMetaDetails & { creationDate?: string } = {
                ...activityMetadata,
                activityInstanceId: result.activityInstanceId,
            };
            await onUpdateActivityMetadata?.({}, values);
        }
    };

    useEffect(() => {
        handleModalState();
    }, [uploadStatus]);

    useEffect(() => {
        if (modalState.status === "continue") {
            form.resetFields();
        }
    }, [modalState.status]);

    const handleModalState = () => {
        switch (uploadStatus) {
            case "completed":
                setModalState({ ...modalState, status: IModalState.Complete });
                break;
            case "pending":
            case "initiated":
            case "created":
                setModalState({ ...modalState, status: IModalState.Uploading });
                break;
            case "aborted":
                resetUpload();
                break;
            case "error":
                setModalState({ ...modalState, status: IModalState.Error });
                break;
            default:
                if (uploadStatus === "idle" && modalState.status === IModalState.Continue) {
                    break;
                }
                setModalState({ ...modalState, status: IModalState.Standard });
                break;
        }
    };

    const renderUploadState = () => {
        switch (modalState.status) {
            case "standard":
            default:
                return (
                    <div className="upload-video">
                        <Upload.Dragger {...uploadProps}>
                            <Row gutter={[16, 16]} style={{ height: "100%" }}>
                                <Col span={24} />
                                <Col span={24} className="flex flex-column">
                                    <UploadOutlined />
                                    <Typography.Title level={3}>Upload your video</Typography.Title>
                                    <Button className="dark">Select video file</Button>
                                </Col>
                                <Col span={24} className="flex flex-column flex-end">
                                    <div>Drag and drop video files to upload</div>
                                    <div className="ant-upload-hint">Please use AVI, MP4 or MOV format under 100MB</div>
                                </Col>
                            </Row>
                        </Upload.Dragger>
                    </div>
                );
            case "continue":
            case "uploading":
                return (
                    <div style={{ width: "100%" }}>
                        <div className="header">
                            <Row style={{ width: "100%" }}>
                                <Col span={8} className="delete">
                                    <IconButton
                                        className="secondary border-grey flex"
                                        onClick={reset}
                                        disabled={uploadStatus !== "idle"}
                                        icon={<DeleteOutlined style={{ fontSize: "18px" }} />}
                                    >
                                        Delete
                                    </IconButton>
                                </Col>
                                <Col span={8} className="title">
                                    <Typography.Title level={5}>Upload your video</Typography.Title>
                                    <Typography.Text>{modalState.pendingFile?.name}</Typography.Text>
                                </Col>
                                <Col span={8} className="saveandexit">
                                    <Button
                                        className="secondary border-grey"
                                        onClick={modalState.status === "continue" ? handleSaveAndExit : handleClose}
                                    >
                                        Save & exit
                                    </Button>
                                </Col>
                            </Row>
                        </div>
                        <Divider />
                        <div className="content">
                            <div className="upload-video-form">
                                <Form
                                    form={form}
                                    disabled={uploadStatus !== "idle"}
                                    initialValues={modalState.metadata}
                                >
                                    <Form.Item
                                        label="Title"
                                        name="title"
                                        rules={[{ required: true, message: "please input a title for your video" }]}
                                        labelCol={{ span: 24 }}
                                        wrapperCol={{ span: 24 }}
                                    >
                                        <Input placeholder="Add a title for your video" />
                                    </Form.Item>
                                    <Form.Item
                                        label="Description"
                                        name="description"
                                        labelCol={{ span: 24 }}
                                        wrapperCol={{ span: 24 }}
                                    >
                                        <Input.TextArea rows={4} placeholder="A short description of your activity" />
                                    </Form.Item>
                                    <Form.Item
                                        name="creationDate"
                                        label="Date Recorded"
                                        tooltip={"Date recorded cannot be changed."}
                                        initialValue={moment.utc(modalState.pendingFile?.lastModified)}
                                        labelCol={{ span: 24 }}
                                        wrapperCol={{ span: 24 }}
                                    >
                                        <DatePicker format={dateFormatList} disabled={true} />
                                    </Form.Item>
                                    <Form.Item
                                        name="tags"
                                        label="Tags"
                                        labelCol={{ span: 24 }}
                                        wrapperCol={{ span: 24 }}
                                    >
                                        <Select mode="tags" />
                                    </Form.Item>
                                    {isDevelopment() ? (
                                        <Form.Item
                                            label="Make Public"
                                            name="isPublic"
                                            tooltip={"This will make the video available to everyone on the platform."}
                                            valuePropName="checked"
                                        >
                                            <Checkbox />
                                        </Form.Item>
                                    ) : null}
                                    {canShowSelect ? (
                                        <>
                                            <Form.Item
                                                label="Make CME Public"
                                                name="isCMEPublic"
                                                tooltip={
                                                    "This will allow anyone with access to the video able to earn cme points from this activity."
                                                }
                                                valuePropName="checked"
                                            >
                                                <Checkbox onChange={() => setIsCMEPublic(!isCMEPublic)} />
                                            </Form.Item>
                                            {isCMEPublic ? (
                                                <Form.Item
                                                    label="Select Questionnaire"
                                                    name="questionnaireId"
                                                    labelCol={{ span: 24 }}
                                                    wrapperCol={{ span: 24 }}
                                                >
                                                    <Select defaultValue={questionnaireAssociationsActive[0]?.title}>
                                                        {questionnaireAssociationsActive.map((questionnaire) => (
                                                            <Select.Option key={questionnaire.questionnaireId}>
                                                                {questionnaire.title}
                                                            </Select.Option>
                                                        ))}
                                                    </Select>
                                                </Form.Item>
                                            ) : null}
                                        </>
                                    ) : null}
                                </Form>
                            </div>
                        </div>
                        <Divider />
                        <div className="footer">
                            {modalState.status === "continue" ? (
                                <Button className="dark" onClick={handleUpdate}>
                                    Upload video
                                </Button>
                            ) : (
                                <>
                                    <div className="flex">
                                        <div style={{ flexGrow: 1 }}>
                                            <Typography.Text strong>Uploading your video</Typography.Text>
                                            <Progress percent={percentage} />
                                        </div>
                                        <IconButton
                                            className="secondary danger"
                                            style={{ color: "red", border: "1px solid red" }}
                                            onClick={handleAbortUpload}
                                            icon={<Clear />}
                                            type="link"
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                );
            case "complete":
                return (
                    <div style={{ width: "100%" }}>
                        <div className="header flex flex-justify-end">
                            <Button className="secondary border-grey" onClick={handleClose}>
                                Save & exit
                            </Button>
                        </div>
                        <Divider />
                        <div className="upload-complete flex flex-column">
                            <DownloadDoneOutlined />
                            <Typography.Title level={3}>Video upload complete</Typography.Title>
                            <Typography.Text>{modalState.pendingFile?.name}</Typography.Text>
                            <div className="flex" style={{ gap: "20px", marginTop: "50px" }}>
                                <Button className="secondary" onClick={reset}>
                                    Upload another
                                </Button>
                                <Button
                                    className="dark"
                                    onClick={() => {
                                        if (isPresent(userId) && isPresent(modalState.activityInstanceId)) {
                                            setSearchParams({
                                                user: userId,
                                                activity: modalState.activityInstanceId,
                                            });
                                        }
                                    }}
                                >
                                    View video
                                </Button>
                            </div>
                        </div>
                    </div>
                );
            case "error":
                return (
                    <div className="upload-video">
                        <Upload.Dragger {...resumeUploadProps}>
                            <Row gutter={[16, 16]} style={{ height: "100%" }}>
                                <Col span={24} />
                                <Col span={24} className="flex flex-column">
                                    <ErrorOutlineOutlined />
                                    <Typography.Title level={5}>
                                        We encountered an error. Want to resume your upload?
                                    </Typography.Title>
                                    <Typography.Text style={{ marginBottom: "20px" }}>
                                        {modalState.pendingFile?.name}
                                    </Typography.Text>
                                    <div style={{ display: "flex", gap: "10px", flexDirection: "column" }}>
                                        <Button className="dark" style={{ flex: 1 }}>
                                            Reselect file to continue
                                        </Button>
                                        <span style={{ alignSelf: "center" }}>Or</span>
                                        <Button className="secondary" style={{ flex: 1 }} onClick={reset}>
                                            Choose another
                                        </Button>
                                    </div>
                                </Col>
                                <Col span={24} className="flex flex-column flex-end">
                                    <div>Drag and drop video files to upload</div>
                                    <div className="ant-upload-hint">Please use AVI, MP4 or MOV format under 100MB</div>
                                </Col>
                            </Row>
                        </Upload.Dragger>
                    </div>
                );
        }
    };

    return (
        <section className="activity-upload">
            <div className="upload-container">{renderUploadState()}</div>
        </section>
    );
};

export default ActivityUpload;
